/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { type BenefitType } from '@strata/constants';
import { DateFormats, formatDate, nullToUndefined } from '@strata/helper-functions';
import { type GridWithFilters } from '@strata/react-grid';
import { type Answer } from '@strata/react-question-tree';
import {
    type AuthRequestStatusIndicator,
    type IntakeStatusIndicator,
} from '@strata/react-status-indicators';
import { type Timeline } from '@strata/react-timeline';
import { type ComponentProps, type ReactNode } from 'react';

import { INTAKE_CASE_API_URL } from 'api/APIBase';
import {
    type IntakeCaseSnoozeStatus,
    type IntakeSource,
    type IntakeStatusDetails,
} from 'services/constants';
import { handleRTKQueryError } from 'services/Error';
import { type TimeFrameOption } from 'services/interfaces';
import { getBaseQuery, jsonToFormData } from 'store/api/apiBaseQuery';
import { FormSections, type IntakeFormSchema } from 'store/intakeForm/intakeFormSchema';

const TagType = {
    INTAKE_SOURCE_LIST: 'INTAKE_SOURCE_LIST',
    INTAKE_CASE_LIST: 'INTAKE_CASE_LIST',
    INTAKE_CASES: 'INTAKE_CASES',
    NOTEBOOKS: 'NOTEBOOKS',
    NOTES: 'NOTES',
    FAX_STATUSES: 'FAX_STATUSES',
    INTAKE_CASE_MILESTONES: 'INTAKE_CASE_MILESTONES',
    UNIT_OF_MEASURE: 'UNIT_OF_MEASURE',
} as const;

export interface IntakeSourceSummary {
    intake_source_id: string;
    fax_number: string;
    sent_by?: string;
    received_date: string;
    archived_date?: string;
    intake_case_id?: string;
    member_first_name?: string;
    member_last_name?: string;
    member_id?: string;
    prescriber_npi?: string;
    prescriber_first_name?: string;
    prescriber_last_name?: string;
}

export interface IntakeCaseSummary {
    intake_case_id: string;
    member_first_name: string;
    member_last_name: string;
    member_id: string;
    member_verified: boolean;
    member_eligible: boolean;
    benefit?: BenefitType;
    product_service_oid?: string;
    is_brand?: boolean;
    diagnosis_code?: string;
    payer_id: string;
    segment_id: string;
    create_date: string;
    submit_date?: string;
    archived_date?: string;
    prescriber_npi?: string;
    prescriber_first_name?: string;
    prescriber_last_name?: string;
    prescriber_verified?: boolean;
    intake_case_sla_override?: number;
    intake_case_archive_override?: number;
    intake_status: (typeof IntakeStatusIndicator.Statuses)[keyof typeof IntakeStatusIndicator.Statuses];
    intake_status_details?: (typeof IntakeStatusDetails)[keyof typeof IntakeStatusDetails];
    auth_request_status?: (typeof AuthRequestStatusIndicator.Statuses)[keyof typeof AuthRequestStatusIndicator.Statuses];
    paused_at?: string;
    paused_reason?: string;
    reminder_date?: string;
    submission_timer_basis: string;
    intake_case_snooze_status: undefined;
    assigned_username?: string;
    updated_date: string;
    non_formulary?: boolean;
    member_gender?: string;
    member_city?: string;
    member_state?: string;
    member_zipcode?: string;
    member_address1?: string;
    member_address2?: string;
    carrier?: string;
    account?: string;
    benefit_group?: string;
}

export const FaxType = {
    PRESCRIBER_INITIATION: 'PRESCRIBER_INITIATION',
    PRIOR_AUTH_NOTIFICATION: 'PRIOR_AUTH_NOTIFICATION',
} as const;

export type FaxTypeKeys = keyof typeof FaxType;

interface FaxResponseSummary {
    fax_status: string;
    fax_type: string;
    fax_response?: string;
    date_sent: string;
    callback_fax_status?: string;
    callback_error_code?: string;
}

export interface IntakeCase {
    intakeCaseId: string;
    createDate: string;
    submitDate?: string;
    archivedDate?: string;
    intakeSource: (typeof IntakeSource)[keyof typeof IntakeSource];
    contactFirstName: string;
    contactLastName: string;
    contactOfficeName: string;
    contactPhoneNumber: string;
    contactFaxNumber?: string;
    contactEmail?: string;
    urgent?: boolean;
    intakeSourceId?: string;
    payerId: string;
    segmentId: string;
    memberId: string;
    memberFirstName: string;
    memberLastName: string;
    memberDateOfBirth: string;
    memberVerified: boolean;
    memberEligible: boolean;
    memberPhoneNumber?: string;
    benefit?: BenefitType;
    placeOfService?: string;
    preferredPharmacyNpi?: string;
    productServiceOid?: string;
    productServiceId?: string;
    productServiceDescription?: string;
    isBrand?: boolean;
    quantity?: number;
    strength?: string;
    productUnits?: number;
    productUom?: string;
    daysSupply?: number;
    refills?: number;
    rxSig?: string;
    diagnosisOid?: string;
    diagnosisCode?: string;
    needsByDate?: string;
    buyAndBill?: boolean;
    sentToPharmacy?: boolean;
    sentToPharmacyNpi?: string;
    additionalMedications?: string[];
    languagePreference?: string;
    prescriberNpi?: string;
    prescriberFirstName?: string;
    prescriberLastName?: string;
    prescriberPhoneNumber?: string;
    prescriberFaxNumber?: string;
    prescriberAddress1?: string;
    prescriberAddress2?: string;
    prescriberCity?: string;
    prescriberState?: string;
    prescriberZip?: string;
    prescriberPracticeNpi?: string;
    prescriberPracticeName?: string;
    prescriberPracticePhoneNumber?: string;
    prescriberPracticeFaxNumber?: string;
    prescriberPracticeAddress1?: string;
    prescriberPracticeAddress2?: string;
    prescriberPracticeCity?: string;
    prescriberPracticeState?: string;
    prescriberPracticeZip?: string;
    prescriberVerified?: boolean;
    clinicalPolicyAnswers?: Answer[];
    defaultClinicalAnswers?: Answer[];
    clinicalFiles: {
        intakeCaseClinicalFileId: string;
        name: string;
        dateAdded: string;
        size: number;
    }[];
    outreaches: {
        outreachOrganization: string;
        communicationDirection: string;
        outreachDate: string;
        upn: string;
        isComplete: boolean;
        actionPlan?: string;
        note?: string;
        communicationType?: string;
    }[];
    authId?: string;
    intakeCaseSlaOverride?: number;
    intakeCaseArchiveOverride?: number;
    intakeStatus: (typeof IntakeStatusIndicator.Statuses)[keyof typeof IntakeStatusIndicator.Statuses];
    intakeStatusDetails?: (typeof IntakeStatusDetails)[keyof typeof IntakeStatusDetails];
    authRequestStatus?: (typeof AuthRequestStatusIndicator.Statuses)[keyof typeof AuthRequestStatusIndicator.Statuses];
    pausedAt?: string;
    pausedReason?: string;
    reminderDate?: string;
    submissionTimerBasis: string;
    assignedUsername?: string;
    nonFormulary?: boolean;
    monitoringCase?: boolean;
    memberGender?: string;
    memberState?: string;
    memberCity?: string;
    memberZipcode?: string;
    memberAddress1?: string;
    memberAddress2?: string;
    carrier?: string;
    account?: string;
    benefitGroup?: string;
    bin?: string;
    pcn?: string;
    rxGroup?: string;
    strengthUnitOfMeasure?: string;
    quantityUnitOfMeasure?: string;
    prescriberPref?: string;
}

export const FaxStatus = {
    SUCCESS: 'Success',
    FAILED: 'Failed',
    NOT_SENT: 'Not Sent',
    NON_PROD: 'Non-Prod',
} as const;

type FaxStatusKeys = keyof typeof FaxStatus;
export type FaxStatusValues = (typeof FaxStatus)[FaxStatusKeys];

export interface FaxResponse {
    faxStatus: (typeof FaxStatus)[keyof typeof FaxStatus];
    faxType: (typeof FaxType)[keyof typeof FaxType];
    faxResponse?: string;
    dateSent: string;
    callbackFaxStatus?: (typeof FaxStatus)[keyof typeof FaxStatus];
    callbackErrorCode?: string;
}

export interface UnitOfMeasure {
    unitOfMeasureId: string;
    unitType: string;
    displayName: string;
    abbreviation: string;
    description: string;
    unitCode: string;
}

export interface UnitOfMeasureResponse {
    strengthUom: UnitOfMeasure[];
    quantityUom: UnitOfMeasure[];
}

export interface TooltipContent {
    data?: {
        date: string;
        username?: string;
        action_plan?: string;
        note?: string;
        communication_direction?: string;
        outreach_organization?: string;
        communication_type?: string;
        submit_method?: string;
        submit_id?: string;
        canceled_reason?: string;
    };
}

export interface IntakeCaseMilestone {
    event_type: (typeof Timeline.EventTypes)[keyof typeof Timeline.EventTypes];
    tooltip_data?: { date: string; username?: string; action_plan?: string };
    title: string;
    timestamp: string;
    status: (typeof Timeline.Statuses)[keyof typeof Timeline.Statuses];
    userType?: (typeof Timeline.UserTypes)[keyof typeof Timeline.UserTypes];
    username?: string;
    message?: string;
    additional_data: { epa_transaction_id?: string };
}

export interface MappedMilestone {
    title: string;
    status: string;
    timestamp: string;
    eventType: (typeof Timeline.EventTypes)[keyof typeof Timeline.EventTypes];
    additionalContent: ReactNode;
    userType?: (typeof Timeline.UserTypes)[keyof typeof Timeline.UserTypes];
    tooltipContent: ReactNode;
    additionalData: { epa_transaction_id?: string };
    action?: {
        label: string;
        onClick: () => void;
    };
}

export const intakeCaseApi = createApi({
    baseQuery: getBaseQuery(INTAKE_CASE_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'intakeCaseApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        postSaveIntakeCase: build.mutation<string, IntakeFormSchema>({
            query: (formValues) => ({
                url: `/intake_cases/create`,
                method: 'POST',
                body: jsonToFormData({
                    intake_source: formValues[FormSections.POINT_OF_CONTACT].intakeSource,
                    contact_first_name: formValues[FormSections.POINT_OF_CONTACT].contactFirstName,
                    contact_last_name: formValues[FormSections.POINT_OF_CONTACT].contactLastName,
                    contact_office_name:
                        formValues[FormSections.POINT_OF_CONTACT].contactOfficeName,
                    contact_phone_number:
                        formValues[FormSections.POINT_OF_CONTACT].contactPhoneNumber,
                    contact_fax_number: formValues[FormSections.POINT_OF_CONTACT].contactFaxNumber,
                    contact_email: formValues[FormSections.POINT_OF_CONTACT].contactEmail,
                    urgent: formValues[FormSections.POINT_OF_CONTACT].urgent,
                    intake_source_id: formValues[FormSections.POINT_OF_CONTACT].intakeSourceId,
                    payer_id: formValues[FormSections.HEALTH_PLAN_AND_PATIENT].payerId,
                    segment_id: formValues[FormSections.HEALTH_PLAN_AND_PATIENT].segmentId,
                    member_id: formValues[FormSections.HEALTH_PLAN_AND_PATIENT].memberId,
                    member_first_name:
                        formValues[FormSections.HEALTH_PLAN_AND_PATIENT].memberFirstName,
                    member_last_name:
                        formValues[FormSections.HEALTH_PLAN_AND_PATIENT].memberLastName,
                    member_date_of_birth: formatDate(
                        formValues[FormSections.HEALTH_PLAN_AND_PATIENT].memberDateOfBirth,
                        DateFormats.HYPHEN_DATE,
                    ),
                    member_verified:
                        formValues[FormSections.HEALTH_PLAN_AND_PATIENT].memberVerified,
                    member_eligible:
                        formValues[FormSections.HEALTH_PLAN_AND_PATIENT].memberEligible,
                    member_phone_number:
                        formValues[FormSections.HEALTH_PLAN_AND_PATIENT].memberPhoneNumber,
                    bin: formValues[FormSections.PATIENT].bin,
                    pcn: formValues[FormSections.PATIENT].pcn,
                    rx_group: formValues[FormSections.PATIENT].rxGroup,
                    benefit: formValues[FormSections.PRODUCT].benefit,
                    place_of_service: formValues[FormSections.PRODUCT].placeOfService,
                    preferred_pharmacy_npi: formValues[FormSections.PRODUCT].preferredPharmacyNpi,
                    product_service_oid: formValues[FormSections.PRODUCT].productServiceOid,
                    product_service_id: formValues[FormSections.PRODUCT].productServiceId,
                    product_service_description:
                        formValues[FormSections.PRODUCT].productServiceDescription,
                    is_brand: formValues[FormSections.PRODUCT].isBrand,
                    quantity: formValues[FormSections.PRODUCT].quantity,
                    strength: formValues[FormSections.PRODUCT].strength,
                    product_units: formValues[FormSections.PRODUCT].productUnits,
                    product_uom: formValues[FormSections.PRODUCT].productUom,
                    days_supply: formValues[FormSections.PRODUCT].daysSupply,
                    refills: formValues[FormSections.PRODUCT].refills,
                    rx_sig: formValues[FormSections.PRODUCT].rxSig,
                    diagnosis_oid: formValues[FormSections.PRODUCT].diagnosisOid,
                    diagnosis_code: formValues[FormSections.PRODUCT].diagnosisCode,
                    needs_by_date: formatDate(
                        formValues[FormSections.PRODUCT].needsByDate,
                        DateFormats.HYPHEN_DATE,
                    ),
                    buy_and_bill: formValues[FormSections.PRODUCT].buyAndBill,
                    sent_to_pharmacy_npi: formValues[FormSections.PRODUCT].sentToPharmacyNpi,
                    additional_medications: formValues[FormSections.PRODUCT].additionalMedications,
                    strength_uom: formValues[FormSections.PRODUCT].strengthUnitOfMeasure,
                    quantity_uom: formValues[FormSections.PRODUCT].quantityUnitOfMeasure,
                    prescriber_npi: formValues[FormSections.PRESCRIBER].prescriberNpi,
                    prescriber_first_name: formValues[FormSections.PRESCRIBER].prescriberFirstName,
                    prescriber_last_name: formValues[FormSections.PRESCRIBER].prescriberLastName,
                    prescriber_phone_number:
                        formValues[FormSections.PRESCRIBER].prescriberPhoneNumber,
                    prescriber_fax_number: formValues[FormSections.PRESCRIBER].prescriberFaxNumber,
                    prescriber_address_1:
                        formValues[FormSections.PRESCRIBER].prescriberAddress1?.trim(),
                    prescriber_address_2:
                        formValues[FormSections.PRESCRIBER].prescriberAddress2?.trim(),
                    prescriber_city: formValues[FormSections.PRESCRIBER].prescriberCity?.trim(),
                    prescriber_state: formValues[FormSections.PRESCRIBER].prescriberState?.trim(),
                    prescriber_zip: formValues[FormSections.PRESCRIBER].prescriberZip?.trim(),
                    prescriber_practice_npi:
                        formValues[FormSections.PRESCRIBER].prescriberPracticeNpi,
                    prescriber_practice_name:
                        formValues[FormSections.PRESCRIBER].prescriberPracticeName,
                    prescriber_practice_phone_number:
                        formValues[FormSections.PRESCRIBER].prescriberPracticePhoneNumber,
                    prescriber_practice_fax_number:
                        formValues[FormSections.PRESCRIBER].prescriberPracticeFaxNumber,
                    prescriber_practice_address_1:
                        formValues[FormSections.PRESCRIBER].prescriberPracticeAddress1?.trim(),
                    prescriber_practice_address_2:
                        formValues[FormSections.PRESCRIBER].prescriberPracticeAddress2?.trim(),
                    prescriber_practice_city:
                        formValues[FormSections.PRESCRIBER].prescriberPracticeCity?.trim(),
                    prescriber_practice_state:
                        formValues[FormSections.PRESCRIBER].prescriberPracticeState?.trim(),
                    prescriber_practice_zip:
                        formValues[FormSections.PRESCRIBER].prescriberPracticeZip?.trim(),
                    prescriber_verified: formValues[FormSections.PRESCRIBER].prescriberVerified,
                    clinical_files: [
                        ...((formValues[FormSections.POINT_OF_CONTACT].uploadedFaxAttachment ??
                            []) as File[]),
                    ],
                }),
            }),
            transformResponse: (response: { inserted_id: string }) => response.inserted_id,
            transformErrorResponse: handleRTKQueryError('An error occurred saving intake case.'),
            invalidatesTags: (_, error) =>
                !error ? [TagType.INTAKE_CASE_LIST, TagType.INTAKE_SOURCE_LIST] : [],
        }),
        putUpdateIntakeCase: build.mutation<string, IntakeCase>({
            query: (intakeCase) => ({
                url: `/intake_cases/${intakeCase.intakeCaseId}`,
                method: 'PUT',
                body: {
                    intake_source: intakeCase.intakeSource,
                    contact_first_name: intakeCase.contactFirstName,
                    contact_last_name: intakeCase.contactLastName,
                    contact_office_name: intakeCase.contactOfficeName,
                    contact_phone_number: intakeCase.contactPhoneNumber,
                    contact_fax_number: intakeCase.contactFaxNumber,
                    contact_email: intakeCase.contactEmail,
                    urgent: intakeCase.urgent,
                    intake_source_id: intakeCase.intakeSourceId,
                    payer_id: intakeCase.payerId,
                    segment_id: intakeCase.segmentId,
                    member_id: intakeCase.memberId,
                    member_first_name: intakeCase.memberFirstName,
                    member_last_name: intakeCase.memberLastName,
                    member_date_of_birth: formatDate(
                        intakeCase.memberDateOfBirth,
                        DateFormats.HYPHEN_DATE,
                    ),
                    member_verified: intakeCase.memberVerified,
                    member_eligible: intakeCase.memberEligible,
                    member_phone_number: intakeCase.memberPhoneNumber,
                    benefit: intakeCase.benefit,
                    place_of_service: intakeCase.placeOfService,
                    preferred_pharmacy_npi: intakeCase.preferredPharmacyNpi,
                    product_service_oid: intakeCase.productServiceOid,
                    product_service_id: intakeCase.productServiceId,
                    product_service_description: intakeCase.productServiceDescription,
                    is_brand: intakeCase.isBrand,
                    quantity: intakeCase.quantity,
                    strength: intakeCase.strength,
                    product_units: intakeCase.productUnits,
                    product_uom: intakeCase.productUom,
                    days_supply: intakeCase.daysSupply,
                    refills: intakeCase.refills,
                    rx_sig: intakeCase.rxSig,
                    diagnosis_oid: intakeCase.diagnosisOid,
                    diagnosis_code: intakeCase.diagnosisCode,
                    needs_by_date: formatDate(intakeCase.needsByDate, DateFormats.HYPHEN_DATE),
                    buy_and_bill: intakeCase.buyAndBill,
                    sent_to_pharmacy: intakeCase.sentToPharmacy,
                    sent_to_pharmacy_npi: intakeCase.sentToPharmacyNpi,
                    additional_medications: intakeCase.additionalMedications,
                    language_preference: intakeCase.languagePreference,
                    prescriber_npi: intakeCase.prescriberNpi,
                    prescriber_first_name: intakeCase.prescriberFirstName,
                    prescriber_last_name: intakeCase.prescriberLastName,
                    prescriber_phone_number: intakeCase.prescriberPhoneNumber,
                    prescriber_fax_number: intakeCase.prescriberFaxNumber,
                    prescriber_address_1: intakeCase.prescriberAddress1?.trim(),
                    prescriber_address_2: intakeCase.prescriberAddress2?.trim(),
                    prescriber_city: intakeCase.prescriberCity?.trim(),
                    prescriber_state: intakeCase.prescriberState?.trim(),
                    prescriber_zip: intakeCase.prescriberZip?.trim(),
                    prescriber_practice_npi: intakeCase.prescriberPracticeNpi,
                    prescriber_practice_name: intakeCase.prescriberPracticeName,
                    prescriber_practice_phone_number: intakeCase.prescriberPracticePhoneNumber,
                    prescriber_practice_fax_number: intakeCase.prescriberPracticeFaxNumber,
                    prescriber_practice_address_1: intakeCase.prescriberPracticeAddress1,
                    prescriber_practice_address_2: intakeCase.prescriberPracticeAddress2,
                    prescriber_practice_city: intakeCase.prescriberPracticeCity,
                    prescriber_practice_state: intakeCase.prescriberPracticeState,
                    prescriber_practice_zip: intakeCase.prescriberPracticeZip,
                    prescriber_verified: intakeCase.prescriberVerified,
                    clinical_policy_answers: JSON.stringify(intakeCase.clinicalPolicyAnswers),
                    additional_clinical_info: JSON.stringify(intakeCase.defaultClinicalAnswers),
                    non_formulary: intakeCase.nonFormulary,
                    member_gender: intakeCase.memberGender,
                    member_city: intakeCase.memberCity?.trim(),
                    member_state: intakeCase.memberState?.trim(),
                    member_zipcode: intakeCase.memberZipcode?.trim(),
                    member_address1: intakeCase.memberAddress1?.trim(),
                    member_address2: intakeCase.memberAddress2?.trim(),
                    carrier: intakeCase.carrier,
                    account: intakeCase.account,
                    benefit_group: intakeCase.benefitGroup,
                    bin: intakeCase.bin,
                    pcn: intakeCase.pcn,
                    rx_group: intakeCase.rxGroup,
                    strength_uom: intakeCase.strengthUnitOfMeasure,
                    quantity_uom: intakeCase.quantityUnitOfMeasure,
                },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred saving intake case.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error
                    ? [
                          { type: TagType.INTAKE_CASES, id: intakeCaseId },
                          TagType.INTAKE_CASE_LIST,
                          TagType.INTAKE_SOURCE_LIST,
                      ]
                    : [],
        }),
        postAttachDocuments: build.mutation<void, { intakeCaseId: string; files: File[] }>({
            query: ({ intakeCaseId, files }) => ({
                url: `/intake_cases/${intakeCaseId}/files`,
                method: 'POST',
                body: jsonToFormData({ files }),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred attaching documents.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error ? [{ type: TagType.INTAKE_CASES, id: intakeCaseId }] : [],
        }),
        postReopenIntakeCase: build.mutation<string, string>({
            query: (intakeCaseId) => ({
                url: `/intake_cases/${intakeCaseId}/reopen`,
                method: 'POST',
            }),
            transformResponse: (response: { intake_case_id: string }) => response.intake_case_id,
            transformErrorResponse: handleRTKQueryError('An error occurred reopening Intake Case.'),
            invalidatesTags: (_, error, intakeCaseId) =>
                !error
                    ? [
                          { type: TagType.INTAKE_CASES, id: intakeCaseId },
                          TagType.INTAKE_CASE_LIST,
                          TagType.INTAKE_SOURCE_LIST,
                      ]
                    : [],
        }),
        cancelIntakeCase: build.mutation<void, { intakeCaseId: string; reason: string }>({
            query: ({ intakeCaseId, reason }) => ({
                url: `/intake_cases/${intakeCaseId}/cancel`,
                body: { reason },
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred canceling the intake case.',
            ),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error
                    ? [TagType.INTAKE_CASE_LIST, { type: TagType.INTAKE_CASES, id: intakeCaseId }]
                    : [],
        }),
        getIntakeSources: build.query<
            {
                count?: number;
                intakeSources: IntakeSourceSummary[];
            },
            {
                page: number;
                searchText?: string;
                timeframe: TimeFrameOption;
                archived: boolean;
                memberId?: string;
                memberFirstName?: string;
                memberLastName?: string;
                prescriberNpi?: string;
                intakeCaseId?: string;
                faxNumber?: string;
                sort?: ComponentProps<typeof GridWithFilters>['sortProperties'];
            }
        >({
            query: ({
                page,
                searchText,
                timeframe,
                archived,
                memberId,
                memberFirstName,
                memberLastName,
                prescriberNpi,
                intakeCaseId,
                faxNumber,
                sort,
            }) => ({
                url: '/intake_sources',
                params: {
                    page,
                    search_text: searchText,
                    timeframe,
                    archived,
                    member_id: memberId,
                    member_first_name: memberFirstName,
                    member_last_name: memberLastName,
                    prescriber_npi: prescriberNpi,
                    intake_case_id: intakeCaseId,
                    fax_number: faxNumber,
                    sort_col: sort?.[0].id,
                    sort_desc: sort?.[0].desc,
                },
            }),
            transformResponse: (response: {
                count?: number;
                intake_sources: IntakeSourceSummary[];
            }) => ({
                count: response?.count,
                intakeSources: response?.intake_sources,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving intake sources.',
            ),
            providesTags: [TagType.INTAKE_SOURCE_LIST],
        }),
        putArchiveIntakeSource: build.mutation<string, string>({
            query: (intakeSourceId) => ({
                url: `/intake_sources/${intakeSourceId}/archive`,
                method: 'PUT',
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred archiving intake source.',
            ),
            invalidatesTags: (_, error) => (!error ? [TagType.INTAKE_SOURCE_LIST] : []),
        }),
        getIntakeCases: build.query<
            {
                count?: number;
                filters?: { key: string; values: string[] }[];
                intakeCases: IntakeCaseSummary[];
            },
            {
                page: number;
                searchText?: string;
                timeframe: TimeFrameOption;
                productServiceOid?: string[];
                segmentId?: string[];
                intakeStatus?: string[];
                intakeStatusDetails?: string[];
                authRequestStatus?: string[];
                memberId?: string;
                memberFirstName?: string;
                memberLastName?: string;
                prescriberNpi?: string;
                intakeCaseId?: string;
                intakeCaseSnoozeStatus?: (typeof IntakeCaseSnoozeStatus)[keyof typeof IntakeCaseSnoozeStatus][];
                assignedUsername?: string[];
                monitoringCase?: boolean;
                sort?: ComponentProps<typeof GridWithFilters>['sortProperties'];
            }
        >({
            query: ({
                page,
                searchText,
                timeframe,
                productServiceOid = [],
                segmentId = [],
                intakeStatus = [],
                intakeStatusDetails = [],
                authRequestStatus = [],
                memberId,
                memberFirstName,
                memberLastName,
                prescriberNpi,
                intakeCaseId,
                intakeCaseSnoozeStatus,
                assignedUsername = [],
                monitoringCase,
                sort,
            }) => ({
                url: '/intake_cases',
                params: {
                    page,
                    search_text: searchText,
                    timeframe,
                    product_service_oid:
                        productServiceOid.length > 0 ? productServiceOid : undefined,
                    segment_id: segmentId.length > 0 ? segmentId : undefined,
                    intake_status: intakeStatus.length > 0 ? intakeStatus : undefined,
                    intake_status_details:
                        intakeStatusDetails.length > 0 ? intakeStatusDetails : undefined,
                    auth_request_status:
                        authRequestStatus.length > 0 ? authRequestStatus : undefined,
                    member_id: memberId,
                    member_first_name: memberFirstName,
                    member_last_name: memberLastName,
                    prescriber_npi: prescriberNpi,
                    intake_case_id: intakeCaseId,
                    intake_case_snooze_status: intakeCaseSnoozeStatus,
                    assigned_username: assignedUsername,
                    monitoring_case: monitoringCase,
                    sort_col: sort?.[0].id,
                    sort_desc: sort?.[0].desc,
                },
            }),
            transformResponse: (response: {
                count?: number;
                filters?: { key: 'product_service_oid' | 'segment_id'; values: string[] }[];
                intake_cases: IntakeCaseSummary[];
            }) => ({
                count: response?.count,
                filters: response?.filters,
                intakeCases: response?.intake_cases,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving intake cases.',
            ),
            providesTags: [TagType.INTAKE_CASE_LIST],
        }),
        getIntakeCaseDetails: build.query<IntakeCase | undefined, { intakeCaseId: string }>({
            query: ({ intakeCaseId }) => `/intake_cases/${intakeCaseId}`,
            transformResponse: (
                response:
                    | {
                          intake_case_id: string;
                          create_date: string;
                          submit_date?: string;
                          archived_date?: string;
                          intake_source: (typeof IntakeSource)[keyof typeof IntakeSource];
                          contact_first_name: string;
                          contact_last_name: string;
                          contact_office_name: string;
                          contact_phone_number: string;
                          contact_fax_number?: string;
                          contact_email?: string;
                          urgent?: boolean;
                          intake_source_id?: string;
                          payer_id: string;
                          segment_id: string;
                          member_id: string;
                          member_first_name: string;
                          member_last_name: string;
                          member_date_of_birth: string;
                          member_verified: boolean;
                          member_eligible: boolean;
                          member_phone_number?: string;
                          benefit?: BenefitType;
                          place_of_service?: string;
                          preferred_pharmacy_npi?: string;
                          product_service_oid?: string;
                          product_service_id?: string;
                          product_service_description?: string;
                          is_brand?: boolean;
                          quantity?: number;
                          strength?: string;
                          product_units?: number;
                          product_uom?: string;
                          days_supply?: number;
                          refills?: number;
                          rx_sig?: string;
                          diagnosis_oid?: string;
                          diagnosis_code?: string;
                          needs_by_date?: string;
                          buy_and_bill?: boolean;
                          sent_to_pharmacy?: boolean;
                          sent_to_pharmacy_npi?: string;
                          additional_medications?: string[];
                          language_preference?: string;
                          prescriber_npi?: string;
                          prescriber_first_name?: string;
                          prescriber_last_name?: string;
                          prescriber_phone_number?: string;
                          prescriber_fax_number?: string;
                          prescriber_address_1?: string;
                          prescriber_address_2?: string;
                          prescriber_city?: string;
                          prescriber_state?: string;
                          prescriber_zip?: string;
                          prescriber_practice_npi?: string;
                          prescriber_practice_name?: string;
                          prescriber_practice_phone_number?: string;
                          prescriber_practice_fax_number?: string;
                          prescriber_practice_address_1?: string;
                          prescriber_practice_address_2?: string;
                          prescriber_practice_city?: string;
                          prescriber_practice_state?: string;
                          prescriber_practice_zip?: string;
                          prescriber_verified?: boolean;
                          clinical_policy_answers?: Answer[];
                          additional_clinical_info?: Answer[];
                          clinical_files: {
                              intake_case_clinical_file_id: string;
                              filename: string;
                              date_added: string;
                              size: number;
                          }[];
                          outreaches: {
                              outreach_organization: string;
                              communication_direction: string;
                              outreach_date: string;
                              upn: string;
                              is_complete: boolean;
                              action_plan?: string;
                              note?: string;
                              communication_type?: string;
                          }[];
                          auth_id?: string;
                          intake_case_sla_override?: number;
                          intake_case_archive_override?: number;
                          intake_status: (typeof IntakeStatusIndicator.Statuses)[keyof typeof IntakeStatusIndicator.Statuses];
                          intake_status_details?: (typeof IntakeStatusDetails)[keyof typeof IntakeStatusDetails];
                          auth_request_status?: (typeof AuthRequestStatusIndicator.Statuses)[keyof typeof AuthRequestStatusIndicator.Statuses];
                          paused_at?: string;
                          paused_reason?: string;
                          reminder_date?: string;
                          submission_timer_basis: string;
                          assigned_username?: string | null;
                          non_formulary?: boolean | null;
                          monitoring_case?: boolean | null;
                          member_gender?: string | null;
                          member_city?: string | null;
                          member_state?: string | null;
                          member_zipcode?: string | null;
                          member_address1?: string | null;
                          member_address2?: string | null;
                          carrier?: string | null;
                          account?: string | null;
                          benefit_group?: string | null;
                          bin?: string | null;
                          pcn?: string | null;
                          rx_group?: string | null;
                          strength_uom?: string | null;
                          quantity_uom?: string | null;
                      }
                    | undefined,
            ) =>
                response
                    ? {
                          intakeCaseId: response.intake_case_id,
                          createDate: response.create_date,
                          submitDate: response.submit_date,
                          archivedDate: response.archived_date,
                          intakeSource: response.intake_source,
                          contactFirstName: response.contact_first_name,
                          contactLastName: response.contact_last_name,
                          contactOfficeName: response.contact_office_name,
                          contactPhoneNumber: response.contact_phone_number,
                          contactFaxNumber: nullToUndefined(response.contact_fax_number),
                          contactEmail: nullToUndefined(response.contact_email),
                          urgent: nullToUndefined(response.urgent),
                          intakeSourceId: nullToUndefined(response.intake_source_id),
                          payerId: response.payer_id,
                          segmentId: response.segment_id,
                          memberId: response.member_id,
                          memberFirstName: response.member_first_name,
                          memberLastName: response.member_last_name,
                          memberDateOfBirth: response.member_date_of_birth,
                          memberVerified: response.member_verified,
                          memberEligible: response.member_eligible,
                          memberPhoneNumber: nullToUndefined(response.member_phone_number),
                          benefit: nullToUndefined(response.benefit),
                          placeOfService: nullToUndefined(response.place_of_service),
                          preferredPharmacyNpi: nullToUndefined(response.preferred_pharmacy_npi),
                          productServiceOid: nullToUndefined(response.product_service_oid),
                          productServiceId: nullToUndefined(response.product_service_id),
                          productServiceDescription: nullToUndefined(
                              response.product_service_description,
                          ),
                          isBrand: nullToUndefined(response.is_brand),
                          quantity: nullToUndefined(response.quantity),
                          strength: nullToUndefined(response.strength),
                          productUnits: nullToUndefined(response.product_units),
                          productUom: nullToUndefined(response.product_uom),
                          daysSupply: nullToUndefined(response.days_supply),
                          refills: nullToUndefined(response.refills),
                          rxSig: nullToUndefined(response.rx_sig),
                          diagnosisOid: nullToUndefined(response.diagnosis_oid),
                          diagnosisCode: nullToUndefined(response.diagnosis_code),
                          needsByDate: nullToUndefined(response.needs_by_date),
                          buyAndBill: nullToUndefined(response.buy_and_bill),
                          sentToPharmacy: nullToUndefined(response.sent_to_pharmacy),
                          sentToPharmacyNpi: nullToUndefined(response.sent_to_pharmacy_npi),
                          additionalMedications: nullToUndefined(response.additional_medications),
                          languagePreference: nullToUndefined(response.language_preference),
                          prescriberNpi: nullToUndefined(response.prescriber_npi),
                          prescriberFirstName: nullToUndefined(response.prescriber_first_name),
                          prescriberLastName: nullToUndefined(response.prescriber_last_name),
                          prescriberPhoneNumber: nullToUndefined(response.prescriber_phone_number),
                          prescriberFaxNumber: nullToUndefined(response.prescriber_fax_number),
                          prescriberAddress1: nullToUndefined(response.prescriber_address_1),
                          prescriberAddress2: nullToUndefined(response.prescriber_address_2),
                          prescriberCity: nullToUndefined(response.prescriber_city),
                          prescriberState: nullToUndefined(response.prescriber_state),
                          prescriberZip: nullToUndefined(response.prescriber_zip),
                          prescriberPracticeNpi: nullToUndefined(response.prescriber_practice_npi),
                          prescriberPracticeName: nullToUndefined(
                              response.prescriber_practice_name,
                          ),
                          prescriberPracticePhoneNumber: nullToUndefined(
                              response.prescriber_practice_phone_number,
                          ),
                          prescriberPracticeFaxNumber: nullToUndefined(
                              response.prescriber_practice_fax_number,
                          ),
                          prescriberPracticeAddress1: nullToUndefined(
                              response.prescriber_practice_address_1,
                          ),
                          prescriberPracticeAddress2: nullToUndefined(
                              response.prescriber_practice_address_2,
                          ),
                          prescriberPracticeCity: nullToUndefined(
                              response.prescriber_practice_city,
                          ),
                          prescriberPracticeState: nullToUndefined(
                              response.prescriber_practice_state,
                          ),
                          prescriberPracticeZip: nullToUndefined(response.prescriber_practice_zip),
                          prescriberVerified: nullToUndefined(response.prescriber_verified),
                          clinicalPolicyAnswers: nullToUndefined(response?.clinical_policy_answers),
                          defaultClinicalAnswers: nullToUndefined(
                              response?.additional_clinical_info,
                          ),
                          clinicalFiles: response.clinical_files?.map((file) => ({
                              intakeCaseClinicalFileId: file.intake_case_clinical_file_id,
                              name: file.filename,
                              dateAdded: file.date_added,
                              size: file.size,
                          })),
                          outreaches:
                              response.outreaches?.map((outreach) => ({
                                  outreachOrganization: outreach.outreach_organization,
                                  communicationDirection: outreach.communication_direction,
                                  outreachDate: outreach.outreach_date,
                                  upn: outreach.upn,
                                  isComplete: outreach.is_complete,
                                  actionPlan: outreach.action_plan,
                                  note: outreach.note,
                                  communicationType: outreach.communication_type,
                              })) ?? [],
                          authId: nullToUndefined(response.auth_id),
                          intakeCaseSlaOverride: nullToUndefined(response.intake_case_sla_override),
                          intakeCaseArchiveOverride: nullToUndefined(
                              response.intake_case_archive_override,
                          ),
                          intakeStatus: response.intake_status,
                          intakeStatusDetails: response.intake_status_details,
                          authRequestStatus: response.auth_request_status,
                          pausedAt: response.paused_at,
                          pausedReason: response.paused_reason,
                          reminderDate: response.reminder_date,
                          submissionTimerBasis: response.submission_timer_basis,
                          assignedUsername: nullToUndefined(response.assigned_username),
                          nonFormulary: nullToUndefined(response.non_formulary),
                          monitoringCase: response.monitoring_case ?? false,
                          memberGender: nullToUndefined(response.member_gender),
                          memberCity: nullToUndefined(response.member_city),
                          memberState: nullToUndefined(response.member_state),
                          memberZipcode: nullToUndefined(response.member_zipcode),
                          memberAddress1: nullToUndefined(response.member_address1),
                          memberAddress2: nullToUndefined(response.member_address2),
                          carrier: nullToUndefined(response.carrier),
                          account: nullToUndefined(response.account),
                          benefitGroup: nullToUndefined(response.benefit_group),
                          bin: nullToUndefined(response.bin),
                          pcn: nullToUndefined(response.pcn),
                          rxGroup: nullToUndefined(response.rx_group),
                          strengthUnitOfMeasure: nullToUndefined(response.strength_uom),
                          quantityUnitOfMeasure: nullToUndefined(response.quantity_uom),
                      }
                    : undefined,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving intake case details.',
            ),
            providesTags: (_result, _error, { intakeCaseId }) => [
                { type: TagType.INTAKE_CASES, id: intakeCaseId },
            ],
        }),
        getConfiguredSegmentIds: build.query<string[], void>({
            query: () => 'payer_segment_config/can_send_prior_auth_request',
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving configured payers.',
            ),
        }),
        // Notes
        getNotebooksForIntakeCase: build.query<
            {
                conversationId: string;
                intakeCaseId: string;
                createdByUpn: string;
                createdTime: string;
                details: { subject: string };
                isUnread: boolean;
            }[],
            { intakeCaseId: string; searchText?: string }
        >({
            query: ({ intakeCaseId, searchText }) => ({
                url: `/notebooks/${intakeCaseId}`,
                params: { search_text: searchText },
            }),
            transformResponse: (response: {
                notebooks: {
                    notebook_id: string;
                    intake_case_id: string;
                    created_by: string;
                    created_time: string;
                    details: { subject: string };
                }[];
            }) =>
                response?.notebooks?.map((notebook) => ({
                    conversationId: notebook.notebook_id,
                    intakeCaseId: notebook.intake_case_id,
                    createdByUpn: notebook.created_by,
                    createdTime: notebook.created_time,
                    details: notebook.details,
                    isUnread: false,
                })),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving notebooks.'),
            providesTags: (_result, _error, { intakeCaseId }) => [
                { type: TagType.NOTEBOOKS, id: intakeCaseId },
            ],
        }),
        postCreateNotebook: build.mutation<
            string,
            { intakeCaseId: string; details: { subject: string } }
        >({
            query: ({ intakeCaseId, details }) => ({
                url: `/notebooks/${intakeCaseId}`,
                method: 'POST',
                body: { details },
            }),
            transformResponse: (response: { inserted_id: string }) => response?.inserted_id,
            transformErrorResponse: handleRTKQueryError('An error occurred creating notebook.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error ? [{ type: TagType.NOTEBOOKS, id: intakeCaseId }] : [],
        }),
        deleteNotebook: build.mutation<void, { intakeCaseId: string; notebookId: string }>({
            query: ({ notebookId }) => ({
                url: `/notebooks/${notebookId}`,
                method: 'DELETE',
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred deleting notebook.'),
            invalidatesTags: (_, error, { intakeCaseId, notebookId }) =>
                !error
                    ? [
                          { type: TagType.NOTEBOOKS, id: intakeCaseId },
                          { type: TagType.NOTES, id: notebookId },
                      ]
                    : [],
        }),
        getNotesForNotebook: build.query<
            {
                messageId: string;
                conversationId: string;
                sentTime: string;
                upn: string;
                content: { message: string };
            }[],
            { notebookId: string }
        >({
            query: ({ notebookId }) => `/notebooks/${notebookId}/notes`,
            transformResponse: (response: {
                notes: {
                    note_id: string;
                    notebook_id: string;
                    sent_by: string;
                    sent_time: string;
                    content: { message: string };
                }[];
            }) =>
                response?.notes?.map((note) => ({
                    messageId: note.note_id,
                    conversationId: note.notebook_id,
                    sentTime: note.sent_time,
                    upn: note.sent_by,
                    content: note.content,
                })),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving notes.'),
            providesTags: (_result, _error, { notebookId }) => [
                { type: TagType.NOTES, id: notebookId },
            ],
        }),
        postCreateNote: build.mutation<
            string,
            { notebookId: string; content: { message: string } }
        >({
            query: ({ notebookId, content }) => ({
                url: `/notebooks/${notebookId}/notes`,
                method: 'POST',
                body: { content },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred creating note.'),
            invalidatesTags: (_, error, { notebookId }) =>
                !error ? [{ type: TagType.NOTES, id: notebookId }] : [],
        }),
        deleteNote: build.mutation<void, { notebookId: string; noteId: string }>({
            query: ({ noteId }) => ({
                url: `/notebooks/notes/${noteId}`,
                method: 'DELETE',
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred deleting note.'),
            invalidatesTags: (_, error, { notebookId }) =>
                !error ? [{ type: TagType.NOTES, id: notebookId }] : [],
        }),
        // Faxes
        getFaxStatuses: build.query<
            Record<(typeof FaxType)[keyof typeof FaxType], FaxResponse>,
            {
                intakeCaseId: string;
            }
        >({
            query: ({ intakeCaseId }) => `/intake_cases/${intakeCaseId}/fax_statuses`,
            transformResponse: (
                response: Record<(typeof FaxType)[keyof typeof FaxType], FaxResponseSummary>,
            ) =>
                Object.entries(response).reduce(
                    (acc, [key, value]) => ({
                        ...acc,
                        [key]: {
                            faxStatus: value.fax_status,
                            faxType: value.fax_type,
                            faxResponse: value.fax_response,
                            dateSent: value.date_sent,
                            callbackFaxStatus: value.callback_fax_status,
                            callbackErrorCode: value.callback_error_code,
                        },
                    }),
                    {} as Record<(typeof FaxType)[keyof typeof FaxType], FaxResponse>,
                ),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving intake case fax responses.',
            ),
            providesTags: (_response, error, { intakeCaseId }) =>
                !error ? [{ type: TagType.FAX_STATUSES, id: intakeCaseId }] : [],
        }),
        postSendFax: build.mutation<
            void,
            { intakeCaseId: string; faxType: FaxTypeKeys; reason?: string; source?: string }
        >({
            query: ({ intakeCaseId, faxType, reason, source }) => ({
                url: `/intake_cases/${intakeCaseId}/send_fax`,
                method: 'POST',
                body: {
                    fax_type: faxType,
                    reason,
                    source,
                },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred sending fax.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error ? [{ type: TagType.FAX_STATUSES, id: intakeCaseId }] : [],
        }),
        postPauseIntakeCase: build.mutation<
            void,
            { intakeCaseId: string; reason: string; reminderDate: Date }
        >({
            query: ({ intakeCaseId, reason, reminderDate }) => ({
                url: `/intake_cases/${intakeCaseId}/pause`,
                method: 'POST',
                body: {
                    reason,
                    reminder_date: formatDate(reminderDate, DateFormats.HYPHEN_DATE),
                },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred pausing intake case.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error
                    ? [TagType.INTAKE_CASE_LIST, { type: TagType.INTAKE_CASES, id: intakeCaseId }]
                    : [],
        }),
        postUnpauseIntakeCase: build.mutation<void, { intakeCaseId: string }>({
            query: ({ intakeCaseId }) => ({
                url: `/intake_cases/${intakeCaseId}/unpause`,
                method: 'POST',
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred resuming intake case.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error
                    ? [TagType.INTAKE_CASE_LIST, { type: TagType.INTAKE_CASES, id: intakeCaseId }]
                    : [],
        }),
        postLogCommunication: build.mutation<
            void,
            {
                intakeCaseId: string;
                outreachOrganization: string;
                outreachDate: string;
                communicationDirection: string;
                completePrescriberOutreach: boolean | undefined;
                actionPlan: string | undefined;
                note: string | undefined;
            }
        >({
            query: ({
                intakeCaseId,
                outreachOrganization,
                outreachDate,
                communicationDirection,
                completePrescriberOutreach,
                actionPlan,
                note,
            }) => ({
                url: `/intake_cases/${intakeCaseId}/outreach`,
                method: 'POST',
                body: {
                    outreach_organization: outreachOrganization,
                    outreach_date: outreachDate,
                    communication_direction: communicationDirection,
                    complete_prescriber_outreach: completePrescriberOutreach,
                    action_plan: actionPlan,
                    note,
                },
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred logging documentation.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error
                    ? [TagType.INTAKE_CASE_LIST, { type: TagType.INTAKE_CASES, id: intakeCaseId }]
                    : [],
        }),
        postLogPriorAuthActivity: build.mutation<
            void,
            {
                intakeCaseId: string;
                priorAuthActivityType: string;
                outreachOrganization: string | undefined;
                priorAuthActivityDate: string | undefined;
                communicationDirection: string;
                communicationType: string | undefined;
                isPrescriberOutreachComplete: boolean | undefined;
                actionPlan: string | undefined;
                isPriorAuthSubmitted: boolean | undefined;
                note: string | undefined;
                submitId: string | undefined;
                submitMethod: string | undefined;
            }
        >({
            query: ({
                intakeCaseId,
                priorAuthActivityType,
                priorAuthActivityDate,
                communicationDirection,
                communicationType,
                isPrescriberOutreachComplete,
                actionPlan,
                isPriorAuthSubmitted,
                note,
                submitId,
                submitMethod,
                outreachOrganization,
            }) => ({
                url: `/intake_cases/${intakeCaseId}/priorAuthActivity`,
                method: 'POST',
                body: {
                    prior_auth_activity_type: priorAuthActivityType,
                    outreach_organization: outreachOrganization,
                    prior_auth_activity_date: priorAuthActivityDate,
                    communication_direction: communicationDirection,
                    communication_type: communicationType,
                    complete_prescriber_outreach: isPrescriberOutreachComplete,
                    action_plan: actionPlan,
                    is_prior_auth_submitted: isPriorAuthSubmitted,
                    note,
                    submit_id: submitId,
                    submit_method: submitMethod,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred logging prior auth activity.',
            ),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error
                    ? [
                          TagType.INTAKE_CASE_LIST,
                          { type: TagType.INTAKE_CASES, id: intakeCaseId },
                          TagType.INTAKE_CASE_MILESTONES,
                      ]
                    : [],
        }),
        // Intake Case Timeline
        getMilestones: build.query<IntakeCaseMilestone[], { intakeCaseId: string }>({
            query: ({ intakeCaseId }) => `/intake_cases/${intakeCaseId}/timeline`,
            transformErrorResponse: handleRTKQueryError('An error occurred fetching milestones.'),
            providesTags: [TagType.INTAKE_CASE_MILESTONES],
        }),
        postCaseAssignment: build.mutation<
            void,
            {
                intakeCaseId: string;
                assignedUsername: string | null | undefined;
            }
        >({
            query: ({ intakeCaseId, assignedUsername }) => ({
                url: `/intake_cases/${intakeCaseId}/case_assignment`,
                method: 'POST',
                body: {
                    assigned_username: assignedUsername,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred assigning the user to the case.',
            ),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error
                    ? [
                          TagType.INTAKE_CASE_LIST,
                          { type: TagType.INTAKE_CASES, id: intakeCaseId },
                          TagType.INTAKE_CASES,
                      ]
                    : [],
        }),
        getPrescriberPreference: build.query<string, { prescriberNpi: string }>({
            query: ({ prescriberNpi }) => `/prescriber/preference/${prescriberNpi}`,
            transformResponse: (response: { prescriber_preference: string }) =>
                response.prescriber_preference,
        }),
        updatePrescriberPreference: build.mutation<
            void,
            { prescriberNpi: string; prescriberPreference: string }
        >({
            query: ({ prescriberNpi, prescriberPreference }) => ({
                url: `/prescriber/preference`,
                method: 'POST',
                params: {
                    prescriber_npi: prescriberNpi,
                    prescriber_preference: prescriberPreference,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred submitting prescriber preference.',
            ),
        }),
        getUnitsOfMeasure: build.query<UnitOfMeasureResponse, void>({
            query: () => `/intake_cases/units_of_measure`,
            transformResponse: (response: {
                strength_uom?: {
                    unit_of_measure_id: string;
                    unit_type: string;
                    display_name: string;
                    abbreviation: string;
                    description: string;
                    unit_code: string;
                }[];
                quantity_uom?: {
                    unit_of_measure_id: string;
                    unit_type: string;
                    display_name: string;
                    abbreviation: string;
                    description: string;
                    unit_code: string;
                }[];
            }): UnitOfMeasureResponse => {
                const mapUom = (u: {
                    unit_of_measure_id: string;
                    unit_type: string;
                    display_name: string;
                    abbreviation: string;
                    description: string;
                    unit_code: string;
                }) => ({
                    unitOfMeasureId: u.unit_of_measure_id,
                    unitType: u.unit_type,
                    displayName: u.display_name,
                    abbreviation: u.abbreviation,
                    description: u.description,
                    unitCode: u.unit_code,
                });

                return {
                    strengthUom: (response.strength_uom ?? []).map(mapUom),
                    quantityUom: (response.quantity_uom ?? []).map(mapUom),
                };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred fetching units of measure.',
            ),
            providesTags: [TagType.UNIT_OF_MEASURE],
        }),
        postPaRequest: build.mutation<
            void,
            {
                intakeCaseId: string;
            }
        >({
            query: ({ intakeCaseId }) => ({
                url: `/intake_cases/${intakeCaseId}/epa`,
                method: 'POST',
                body: {},
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred creating the epa.'),
            invalidatesTags: (_, error, { intakeCaseId }) =>
                !error
                    ? [
                          TagType.INTAKE_CASES,
                          { type: TagType.INTAKE_CASES, id: intakeCaseId },
                          TagType.INTAKE_CASE_MILESTONES,
                      ]
                    : [],
        }),
    }),
});

export const {
    usePostSaveIntakeCaseMutation,
    usePutUpdateIntakeCaseMutation,
    usePostAttachDocumentsMutation,
    usePostReopenIntakeCaseMutation,
    useGetIntakeSourcesQuery,
    usePutArchiveIntakeSourceMutation,
    useGetIntakeCasesQuery,
    useGetIntakeCaseDetailsQuery,
    useGetConfiguredSegmentIdsQuery,
    useGetNotebooksForIntakeCaseQuery,
    usePostCreateNotebookMutation,
    useDeleteNotebookMutation,
    useGetNotesForNotebookQuery,
    usePostCreateNoteMutation,
    useDeleteNoteMutation,
    useGetFaxStatusesQuery,
    usePostSendFaxMutation,
    usePostPauseIntakeCaseMutation,
    usePostUnpauseIntakeCaseMutation,
    useCancelIntakeCaseMutation,
    usePostLogCommunicationMutation,
    usePostLogPriorAuthActivityMutation,
    useGetMilestonesQuery,
    usePostCaseAssignmentMutation,
    useGetPrescriberPreferenceQuery,
    useUpdatePrescriberPreferenceMutation,
    useGetUnitsOfMeasureQuery,
    usePostPaRequestMutation,
} = intakeCaseApi;
